// Library imports
import React, { useState } from 'react';
import { Link } from 'gatsby';
import { Navbar, Dropdown, ButtonGroup, Nav, Offcanvas } from 'react-bootstrap';

// Component imports
import SideBarNav from './SideBarNav';
import AlgoliaSearchWrapper from './Search';

// Data imports
import {
  nav,
  lastSuportedReleases,
  docsFourUrl,
  algoliaVersions,
  homeVersion,
} from '../config/site';
import tocHomeMobile from '../data/TocContent/tocHomeMobile';

// Asset imports
//import logo from '../images/incorta_blue.png';
import logo from '../images/newlogos/Incorta_White_logo.png';


// Style imports
import NavBarStyles from './NavBar.module.scss';
import NavBarOCStyles from './OffCanvasNavBar.module.scss';

/**
 *  NavButton: Function for Navbar button styling
 * @param item Object data for button
 * @param pageVersion The current content version on page
 * @returns
 */
function NavButton({ item, pageVersion }) {
  return (
    <Link
      to={`${item.linkTo}`}
      className={`${NavBarStyles.link} ${
        item.context.includes(pageVersion) ? NavBarStyles.activeLocation : ''
      }`}
    >
      {item.text}
    </Link>
  );
}

function ResourceDropdown({ offcanvas = false }) {
  return (
    <Dropdown as={ButtonGroup}>
      <Dropdown.Toggle
        className={
          offcanvas
            ? NavBarOCStyles.ocResourceButton
            : NavBarStyles.resourceButton
        }
      >
        RESOURCES
      </Dropdown.Toggle>
      <Dropdown.Menu
        className={
          offcanvas ? NavBarOCStyles.ocResourceMenu : NavBarStyles.resourceMenu
        }
      >
        <Dropdown.Item rel="external" href={`https://community.incorta.com/`} target="_blank">
          Community
        </Dropdown.Item>
        <Dropdown.Item rel="external" href={`https://learn.incorta.com/`} target="_blank">
          Learn
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/**
 * NavVersionDropdown: The Docs version dropdown button in the Nav component.
 * @param pageVersion
 * @param offcanvas: Determine styling if in offcanvas sidebar
 * @returns
 */
function NavVersionDropdown({ pageVersion, offcanvas = false }) {
  return (
    <>
      {lastSuportedReleases.includes(pageVersion) && (
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle
            className={
              offcanvas
                ? NavBarOCStyles.ocVersionButton
                : NavBarStyles.versionButton
            }
            id="version-dropdown"
          >
            {`${pageVersion?.toUpperCase()}`}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className={
              offcanvas
                ? NavBarOCStyles.ocVersionMenu
                : NavBarStyles.versionMenu
            }
          >
            {lastSuportedReleases.map((release, key) =>
              algoliaVersions.includes(release) ? (
                <Link to={`/${release}/`} key={key}>
                  <Dropdown.Item as="div">{release}</Dropdown.Item>
                </Link>
              ) : (
                <Dropdown.Item
                  rel="external"
                  href={`${docsFourUrl}/${release}`}
                  key={key}
                >
                  {release}
                </Dropdown.Item>
              ),
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  );
}

/**
 * OffCanvasNavBar: OffCanvasNavBar component for mobile/tablet size
 * @param pageVersion
 * @param siteTocArray
 * @param mobileToc This param defaults to the home Content mobile toc
 * @returns
 */
function OffCanvasNavBar({ pageVersion, siteTocArray, mobileToc }) {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <Navbar.Toggle
        aria-controls="offcanvasNavbar"
        className={NavBarStyles.toggler}
        onClick={handleShow}
      />
      <Offcanvas
        id="offcanvasNavbar"
        aria-labelledby="offcanvasNavbarLabel"
        placement="end"
        className={`${NavBarOCStyles.offcanvasWrapper}`}
        show={show}
        onHide={handleClose}
      >
        <Offcanvas.Header className={NavBarOCStyles.ocHeader} closeButton>
          <img
            alt="Incorta logo"
            src={logo}
            className={NavBarOCStyles.imgLogo}
          />
        </Offcanvas.Header>
        <Offcanvas.Body className={NavBarOCStyles.ocBody}>
          <Nav className={`${NavBarOCStyles.ocNav}`}>
            <Nav.Link as="div" className={NavBarStyles.searchWrapper}>
              {pageVersion !== 'home' ? (
                <AlgoliaSearchWrapper pageVersion={pageVersion} />
              ) : (
                <div
                  style={{
                    display: 'none',
                  }}
                />
              )}
            </Nav.Link>
            {nav.map((item, key) => (
              <Nav.Link key={key} as="div" className={NavBarOCStyles.link}>
                <NavButton item={item} pageVersion={pageVersion} />
              </Nav.Link>
            ))}
            <NavVersionDropdown
              pageVersion={pageVersion}
              offcanvas
              key="versionDropdown"
            />
            <ResourceDropdown offcanvas />
            <Nav.Link
              id="signup"
              className={`signup ${NavBarStyles.signup}`}
              href="https://cloud.incorta.com/signin"
              target="_blank"

            >
              START FOR FREE
            </Nav.Link>
            <hr />
            {pageVersion !== homeVersion ? (
              <>
                <h3
                  className={NavBarOCStyles.ocTitle}
                  id="offcanvasNavbarLabel"
                >
                  Navigation
                </h3>
                <SideBarNav siteTocArray={siteTocArray} />
              </>
            ) : (
              <>
                <h3
                  className={NavBarOCStyles.ocTitle}
                  id="offcanvasNavbarLabel"
                >
                  Navigation
                </h3>
                <SideBarNav siteTocArray={mobileToc} />
              </>
            )}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

/**
 * NavBar: NavBar component
 * @param pageVersion
 * @param siteTocArray
 * @param mobileToc This param defaults to the home Content mobile toc
 * @returns
 */
export default function NavBar({
  pageVersion,
  siteTocArray,
  mobileToc = tocHomeMobile,
}) {
  return (
    <header>
      <Navbar expand="lg" className={NavBarStyles.navbarWrapper}>
        <Navbar.Brand
          className={NavBarStyles.brandWrapper}
          href="https://www.incorta.com/"
          target="_blank"
        >
          <img
            alt="Incorta logo"
            src={logo}
            className={NavBarStyles.imgLogo}
            loading="eager"
          />
        </Navbar.Brand>
        <OffCanvasNavBar
          siteTocArray={siteTocArray}
          pageVersion={pageVersion}
          mobileToc={mobileToc}
        />
        <Nav className={`d-none d-lg-flex ${NavBarStyles.primaryNav}`}>
          {nav.map((item, key) => (
            <Nav.Link key={key} as="div">
              <NavButton item={item} pageVersion={pageVersion} />
            </Nav.Link>
          ))}
          <NavVersionDropdown pageVersion={pageVersion} />
          <Nav.Link as="div" className={NavBarStyles.rightSide}>
            <Nav.Link as="div" className={NavBarStyles.searchWrapper}>
              {pageVersion !== 'home' ? (
                <AlgoliaSearchWrapper
                  pageVersion={pageVersion}
                  key="algoliaWrapper"
                />
              ) : (
                <div
                  key="noAlgolia"
                  style={{
                    display: 'none',
                  }}
                />
              )}
            </Nav.Link>
            <ResourceDropdown />
            <Nav.Link
              id="signup"
              className={`signup ${NavBarStyles.signup}`}
              href="https://cloud.incorta.com/signin"
              target="_blank"
            >
              START FOR FREE
            </Nav.Link>
          </Nav.Link>
        </Nav>
      </Navbar>
    </header>
  );
}
